import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Typography } from '@mui/material';
import { getViewUrl, getMessageViewUrl } from '../../redux/attachments/service';
import Loader from '../../shared/Loader';
import { Container, errorMessageStyle, successMessageStyle } from '../styled';
import {
    LoadingContainer,
    ErrorContainer,
    SuccessContainer,
    StyledButton,
    LoadingText
} from './styled';
import useAppDispatch from 'src/hooks/useAppDispatch';
import { RoutesEnum } from '../../types/navigation';
import { downloadFile } from '../../utils/attachments';

type DownloadStatus = 'loading' | 'downloading' | 'success' | 'error';

const AttachmentPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const { attachmentId } = useParams<{
        attachmentId: string;
    }>();
    const [searchParams] = useSearchParams();
    const attachmentSource = searchParams.get('source');

    const navigate = useNavigate();

    const [status, setStatus] = useState<DownloadStatus>('loading');
    const [message, setMessage] = useState<string>('');

    const getFileNameFromUrl = (url: string): string => {
        try {
            const urlPath = new URL(url).pathname;
            const fileName = urlPath.split('/').pop() || 'download';
            return decodeURIComponent(fileName);
        } catch {
            return 'download';
        }
    };

    const fetchAttachmentUrl = useCallback(async () => {
        try {
            setStatus('loading');
            setMessage('');

            const parsedAttachmentId = attachmentId
                ? parseInt(attachmentId)
                : 0;
            // Missing source defaults to backend for backward compatibility.
            const response =
                attachmentSource === 'waggle'
                    ? await dispatch(
                          getMessageViewUrl({
                              attachmentId: parsedAttachmentId
                          })
                      ).unwrap()
                    : await dispatch(
                          getViewUrl({ fileId: parsedAttachmentId })
                      ).unwrap();

            if (response.url) {
                setStatus('downloading');
                setMessage('Downloading file...');

                try {
                    const fileName =
                        response.name || getFileNameFromUrl(response.url);
                    await downloadFile(response.url, fileName);
                    setStatus('success');
                    setMessage('File downloaded successfully!');
                } catch (downloadError: any) {
                    console.error('Error downloading file:', downloadError);
                    setStatus('error');
                    setMessage(
                        downloadError.message ||
                            'Failed to download file. Please try again.'
                    );
                }
            } else {
                setStatus('error');
                setMessage('No file URL received');
            }
        } catch (err: any) {
            console.error('Error fetching attachment:', err);
            setStatus('error');
            setMessage(
                err.message || 'Failed to load attachment. Please try again.'
            );
        }
    }, [attachmentId, attachmentSource, dispatch]);

    useEffect(() => {
        fetchAttachmentUrl();
    }, [fetchAttachmentUrl]);

    if (status === 'loading' || status === 'downloading') {
        return (
            <Container>
                <LoadingContainer>
                    <Loader />
                    <LoadingText>
                        {status === 'loading'
                            ? 'Loading attachment...'
                            : message}
                    </LoadingText>
                </LoadingContainer>
            </Container>
        );
    }

    if (status === 'error') {
        return (
            <Container>
                <ErrorContainer>
                    <Typography sx={errorMessageStyle}>{message}</Typography>
                    <StyledButton
                        onClick={() => navigate(RoutesEnum.PROJECT_DASHBOARD)}
                    >
                        Back to Main Page
                    </StyledButton>
                </ErrorContainer>
            </Container>
        );
    }

    if (status === 'success') {
        return (
            <Container>
                <SuccessContainer>
                    <Typography sx={successMessageStyle}>{message}</Typography>
                    <StyledButton
                        onClick={() => navigate(RoutesEnum.PROJECT_DASHBOARD)}
                    >
                        Back to Main Page
                    </StyledButton>
                </SuccessContainer>
            </Container>
        );
    }

    return null;
};

export default AttachmentPage;
